import { AlertTriangle, Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

/**
 * The three non-content states every data-fetching view must implement.
 * A blank screen is never acceptable.
 */

export function SkeletonLoader({ className, rows = 3 }: { className?: string; rows?: number }) {
  const { t } = useTranslation();
  return (
    <div className={cn('space-y-3', className)} role="status" aria-live="polite">
      <span className="sr-only">{t('state.loading')}</span>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-4 w-full animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}

export function ErrorMessage({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4" role="alert">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{t('state.errorTitle')}</p>
          {message ? <p className="text-sm text-muted-foreground">{message}</p> : null}
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="text-sm font-medium text-primary underline underline-offset-4"
            >
              {t('state.retry')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function EmptyState({ hint }: { hint?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed p-8 text-center">
      <Inbox className="h-5 w-5 text-muted-foreground" aria-hidden />
      <p className="text-sm font-medium text-foreground">{t('state.emptyTitle')}</p>
      <p className="text-sm text-muted-foreground">{hint ?? t('state.emptyHint')}</p>
    </div>
  );
}
